/**
 * i18n configuration — Path B Phase 1 (Framework setup, 2026-05-26).
 *
 * Single source of truth for the admin SPA's supported locales. Mirrors
 * the svdy-website conventions in spirit but uses react-i18next instead
 * of svdy-website's custom translations.ts (the website's bespoke setup
 * predates the i18n decision; admin SPA gets the industry-standard
 * stack from day 1).
 *
 * Adding a locale is a 3-line change:
 *   1. Append code to SUPPORTED_LOCALES below.
 *   2. Add display label to LOCALE_NAMES.
 *   3. Create src/i18n/locales/<code>/common.json (and any other ns).
 *
 * Then build picks it up automatically (see ./index.ts dynamic loader).
 */

export const SUPPORTED_LOCALES = ['en', 'es', 'pt', 'fr', 'de', 'ja', 'zh-CN', 'ar', 'ko'] as const;
export type SupportedLocale = typeof SUPPORTED_LOCALES[number];

export const DEFAULT_LOCALE: SupportedLocale = 'en';
export const FALLBACK_LOCALE: SupportedLocale = 'en';

/**
 * Native-name display labels (shown in the locale picker). Always native;
 * never anglicized. "Español" not "Spanish", "日本語" not "Japanese".
 */
export const LOCALE_NAMES: Record<SupportedLocale, string> = {
  en: 'English',
  es: 'Español',
  pt: 'Português (BR)',
  fr: 'Français',
  de: 'Deutsch',
  ja: '日本語',
  'zh-CN': '简体中文',
  ar: 'العربية',
  ko: '한국어',
};

/**
 * BCP 47 codes — used by Intl.DateTimeFormat / NumberFormat for locale-
 * sensitive formatting once the strings are translated.
 */
export const LOCALE_BCP47: Record<SupportedLocale, string> = {
  en: 'en-US',
  es: 'es-ES',
  pt: 'pt-BR',
  fr: 'fr-FR',
  de: 'de-DE',
  ja: 'ja-JP',
  'zh-CN': 'zh-CN',
  ar: 'ar-SA',
  ko: 'ko-KR',
};

/** RTL locales — used to set document dir attribute */
export const RTL_LOCALES: SupportedLocale[] = ['ar'];

/**
 * Namespaces — split translation files for lazy loading + code-owner
 * separation. ``common`` is the always-loaded baseline (action verbs,
 * generic UI labels). ``errors`` is the cross-platform error-code
 * catalog (i18n Phase 2) used by translateApiError(). Module-specific
 * namespaces (admin / worker / billing / ...) get added as Phase 4
 * migration progresses.
 */
export const NAMESPACES = ['common', 'errors', 'org', 'auth', 'dashboard', 'system', 'processes', 'attendance', 'integrations', 'worker'] as const;
export type Namespace = typeof NAMESPACES[number];
export const DEFAULT_NAMESPACE: Namespace = 'common';
